import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

export default function UnsubscribePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState<'loading' | 'valid' | 'already' | 'invalid' | 'success' | 'error'>('loading');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      return;
    }

    const validate = async () => {
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
        const response = await fetch(
          `${supabaseUrl}/functions/v1/handle-email-unsubscribe?token=${token}`,
          { headers: { apikey: anonKey } }
        );
        const data = await response.json();

        if (data.valid === false && data.reason === 'already_unsubscribed') {
          setStatus('already');
        } else if (data.valid) {
          setStatus('valid');
        } else {
          setStatus('invalid');
        }
      } catch {
        setStatus('invalid');
      }
    };

    validate();
  }, [token]);

  const handleUnsubscribe = async () => {
    if (!token) return;
    setProcessing(true);
    try {
      const { data, error } = await supabase.functions.invoke('handle-email-unsubscribe', {
        body: { token },
      });

      if (error) throw error;
      setStatus('success');
    } catch {
      setStatus('error');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="max-w-md w-full bg-card rounded-xl shadow-lg overflow-hidden">
        <div className="bg-primary px-6 py-4">
          <h1 className="text-primary-foreground text-lg font-bold tracking-wide">NetUno</h1>
        </div>
        <div className="p-8 text-center">
          {status === 'loading' && (
            <p className="text-muted-foreground">Validando...</p>
          )}

          {status === 'valid' && (
            <>
              <h2 className="text-xl font-semibold text-foreground mb-3">Cancelar Inscrição</h2>
              <p className="text-sm text-muted-foreground mb-6">
                Deseja deixar de receber notificações por e-mail?
              </p>
              <button
                onClick={handleUnsubscribe}
                disabled={processing}
                className="w-full bg-destructive text-destructive-foreground py-3 px-6 rounded-md font-medium hover:opacity-90 disabled:opacity-50 transition-opacity"
              >
                {processing ? 'Processando...' : 'Confirmar Cancelamento'}
              </button>
            </>
          )}

          {status === 'already' && (
            <>
              <h2 className="text-xl font-semibold text-foreground mb-3">Já Cancelado</h2>
              <p className="text-sm text-muted-foreground">
                Você já cancelou a inscrição anteriormente.
              </p>
            </>
          )}

          {status === 'invalid' && (
            <>
              <h2 className="text-xl font-semibold text-foreground mb-3">Link Inválido</h2>
              <p className="text-sm text-muted-foreground">
                Este link de cancelamento não é válido ou já expirou.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <h2 className="text-xl font-semibold text-foreground mb-3">Inscrição Cancelada</h2>
              <p className="text-sm text-muted-foreground">
                Você não receberá mais notificações por e-mail.
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <h2 className="text-xl font-semibold text-foreground mb-3">Erro</h2>
              <p className="text-sm text-muted-foreground">
                Ocorreu um erro ao processar sua solicitação. Tente novamente.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
